import React from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import age18Icon from "../../assets/images/age18+.png";
import Logo from "../../assets/Logo.svg";

const STORAGE_KEY = "age_verification_consent";

interface AgeVerificationProps {
  onVerified: () => void;
}

const AgeVerification: React.FC<AgeVerificationProps> = ({ onVerified }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isSmallHeight = useMediaQuery("(max-height: 820px)");

  const handleEnter = () => {
    // Store consent with timestamp
    const consentData = {
      consented: true,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));
    onVerified();
  };

  const handleExit = () => {
    // Redirect to a safe page or external site
    window.location.href = "https://www.google.com";
  };

  return (
    <Box
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: isMobile ? (isSmallHeight ? "8px" : "16px") : "24px",
        overflowY: "auto",
      }}
    >
      {/* Logo for desktop */}
      {!isMobile && (
        <Box
          style={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            zIndex: 10001,
            width: "100%",
            marginBottom: "48px",
          }}
        >
          <img
            src={Logo}
            alt="Sugar Cribs"
            style={{
              width: isTablet ? "160px" : isLargeScreen ? "180px" : "150px",
              maxWidth: isTablet ? "200px" : isLargeScreen ? "220px" : "180px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
      )}

      <Box
        style={{
          maxWidth: isMobile ? "600px" : "800px",
          width: "100%",
          backgroundColor: "#131313",
          borderRadius: "16px",
          padding: isMobile ? (isSmallHeight ? "16px" : "24px") : "40px",
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
          position: "relative",
        }}
      >
        {/* Logo positioned above modal for mobile */}
        {isMobile && (
          <Box
            style={{
              position: isSmallHeight ? "relative" : "absolute",
              bottom: isSmallHeight ? "auto" : "calc(100% + 24px)",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              justifyContent: "center",
              zIndex: 10001,
              width: "100%",
              padding: isSmallHeight ? "8px 16px" : "0 16px",
              marginBottom: isSmallHeight ? "8px" : "0",
            }}
          >
            <img
              src={Logo}
              alt="Sugar Cribs"
              style={{
                width: isSmallHeight ? "120px" : "150px",
                maxWidth: isSmallHeight ? "140px" : "180px",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        )}
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: isMobile ? "24px" : "20px",
          }}
        >
          <img
            src={age18Icon}
            alt="18+"
            style={{
              width:
                isMobile && isSmallHeight
                  ? "80px"
                  : isMobile
                  ? "100px"
                  : "160px",
              height:
                isMobile && isSmallHeight
                  ? "80px"
                  : isMobile
                  ? "100px"
                  : "160px",
            }}
          />
        </Box>

        <Typography
          style={{
            color: "#ffffff",
            fontSize:
              isMobile && isSmallHeight ? "18px" : isMobile ? "20px" : "32px",
            fontWeight: "bold",
            fontFamily: "Helvetica, Arial, sans-serif",
            marginBottom: isMobile ? "12px" : "20px",
          }}
        >
          Confirm your age
        </Typography>

        <Typography
          style={{
            color: "#FFFFFFCC",
            fontSize: isMobile ? "16px" : "20px",
            lineHeight: "110%",
            fontFamily: "Helvetica, Arial, sans-serif",
            marginBottom: isSmallHeight && isMobile ? "24px" : "60px",
            textAlign: "center",
            maxWidth: "515px",
            margin:
              isSmallHeight && isMobile
                ? "0px auto 24px auto"
                : "0px auto 60px auto",
            marginTop: "0px !important",
          }}
        >
          By clicking "I Am 18 or Older," you confirm that you are{" "}
          <span
            style={{
              color: "#ffffff",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              lineHeight: "100%",
            }}
          >
            at least 18 years old
          </span>{" "}
          (or the legal age of majority in your jurisdiction), that you are
          accessing this website voluntarily, and that you understand this site
          contains explicit adult content intended solely for adults.
          <br />
          <br />
          If you are under the legal age, or do not wish to view such content,
          you must exit this site immediately.
        </Typography>

        <Box
          style={{
            display: "flex",
            flexDirection: isMobile ? "column-reverse" : "row",
            gap: isSmallHeight && isMobile ? "12px" : "16px",
            justifyContent: isMobile ? "center" : "space-between",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleExit}
            style={{
              backgroundColor: "transparent",
              color: "#cccccc",
              fontSize: isSmallHeight && isMobile ? "14px" : "16px",
              fontWeight: 600,
              padding:
                isSmallHeight && isMobile
                  ? "10px 24px"
                  : isMobile
                  ? "14px 32px"
                  : "14px 32px",
              borderRadius: "6px",
              border: "2px solid #FE5A97",
              textTransform: "none",
              minWidth: isMobile ? "100%" : "auto",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = "#2a2a2a";
              e.currentTarget.style.borderColor = "#FE5A97";
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor = "#FE5A97";
              e.currentTarget.style.color = "#cccccc";
            }}
          >
            Exit site
          </Button>

          <Button
            variant="contained"
            onClick={handleEnter}
            style={{
              backgroundColor: "#FE5A97",
              color: "#ffffff",
              fontSize: isSmallHeight && isMobile ? "14px" : "16px",
              fontWeight: 600,
              padding:
                isSmallHeight && isMobile
                  ? "10px 24px"
                  : isMobile
                  ? "14px 32px"
                  : "14px 32px",
              borderRadius: "6px",
              textTransform: "none",
              minWidth: isMobile ? "100%" : "200px",
              boxShadow: "0 4px 12px rgba(254, 90, 151, 0.3)",
            }}
          >
            I Confirm That I Am 18 or Older
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export const hasAgeVerificationConsent = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;

    const consentData = JSON.parse(stored);
    return consentData?.consented === true;
  } catch {
    return false;
  }
};

export const clearAgeVerificationConsent = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export default AgeVerification;
